import { memo, useMemo, useState } from 'react';

import { Drawer } from 'components/drawer';
import { formatPrice } from 'utils/helpers';
import { isMobile, useDownloadFile } from 'hooks';
import { CategoryDetails } from 'store/api/event/types';
import { DownloadButton } from 'components/specified-buttons';

import * as LineStyles from '../line-check/styles';
import { RecordTable, RecordColumn } from '../record-table';
import {
  sumAmounts,
  LineDifference,
  useFitDrawerTop,
  LineDrawerHeader,
  useEscapeToClose,
} from '../line-check';

import { formatRowDate } from './helpers';
import { ExpenseDetails } from './expense-details';
import { CategoryDetailsDialogProps } from './types';
import { ReimbursementDetails } from './reimbursement-details';

const isSelectable = (value: CategoryDetails) =>
  ['expense', 'reimbursement'].includes(value.source);

export const CategoryDetailsDrawer = memo(
  ({
    open,
    onClose,
    eventName,
    lineAmount,
    categoryName,
    categoryDetails,
  }: CategoryDetailsDialogProps) => {
    const mobile = isMobile();
    const { downloadFile } = useDownloadFile();
    const { ref, topOffset } = useFitDrawerTop(mobile);
    const [selectedExpenseId, setSelectedExpenseId] = useState<number>();
    const [selectedReimbursementId, setSelectedReimbursementId] =
      useState<number>();

    const categoryExpenses = useMemo(
      () =>
        categoryDetails?.filter(
          (value) => value.categoryName === categoryName
        ) || [],
      [categoryName, categoryDetails]
    );

    // Every column the drawer had; Notes is the record's description, the
    // one flexible column.
    const columns = useMemo<RecordColumn<CategoryDetails>[]>(
      () => [
        {
          key: 'date',
          kind: 'date',
          title: 'Date',
          value: formatRowDate,
        },
        {
          key: 'notes',
          kind: 'flex',
          title: 'Description',
          value: (row) => row.notes,
        },
        {
          key: 'method',
          kind: 'nowrap',
          title: 'Method',
          value: (row) => row.paymentMethod?.name,
        },
        {
          kind: 'compact',
          key: 'employees',
          title: 'Employees',
          value: (row) => row.employeeName,
        },
        {
          kind: 'compact',
          key: 'createdBy',
          title: 'Created By',
          value: (row) => row.createdBy?.name,
        },
        {
          key: 'amount',
          kind: 'amount',
          title: 'Amount',
          value: (row) => formatPrice(row.amount),
        },
        {
          title: '',
          kind: 'icon',
          key: 'receipt',
          value: (row) =>
            row.receiptUrl && (
              <DownloadButton
                url={row.receiptUrl}
                onDownloadFile={downloadFile}
              />
            ),
        },
      ],
      [downloadFile]
    );

    useEscapeToClose(!!open, onClose);

    if (!open) {
      return null;
    }

    const showList = !selectedReimbursementId && !selectedExpenseId;

    return (
      <Drawer elevated hideOverlay opened={true} topOffset={topOffset}>
        <LineStyles.DrawerContent ref={ref}>
          <LineDrawerHeader
            onClose={onClose}
            subtitle={eventName}
            title={categoryName}
          />

          {showList && (
            <RecordTable
              columns={columns}
              rows={categoryExpenses}
              isRowClickable={isSelectable}
              total={sumAmounts(categoryExpenses)}
              getKey={(row) => `${row.source}-${row.id}-${row.amount}`}
              getRowLabel={(row) =>
                `Open ${row.notes || categoryName} ${formatRowDate(row)} ${formatPrice(row.amount)}`
              }
              onRowClick={(row) =>
                row.source === 'expense'
                  ? setSelectedExpenseId(row.id)
                  : setSelectedReimbursementId(row.id)
              }
            />
          )}

          {showList && (
            <LineDifference
              lineAmount={lineAmount}
              total={sumAmounts(categoryExpenses)}
            />
          )}

          {selectedReimbursementId && (
            <ReimbursementDetails
              selectedReimbursementId={selectedReimbursementId}
              onClose={() => setSelectedReimbursementId(undefined)}
            />
          )}

          {selectedExpenseId && (
            <ExpenseDetails
              selectedExpenseId={selectedExpenseId}
              onClose={() => setSelectedExpenseId(undefined)}
            />
          )}
        </LineStyles.DrawerContent>
      </Drawer>
    );
  }
);
